import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, UserPlus, Loader2 } from 'lucide-react';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { returnsService } from '@/services/returns';
import { platformUsersService } from '@/services/platform/users';
import type { ReturnOrder } from '@/types/returns';
import { fmt } from '@/lib/format';
import { toApiError } from '@/lib/errors';

interface Props {
  returnRequest: ReturnOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Mirror of CancelRequestDetailSheet — for returns in REQUESTED state.
 * Approve / Reject end-states differ (returns don't auto-create a
 * refund — that happens post-QC in Phase 4) but the reviewer pattern
 * is identical.
 */
export function ReturnRequestDetailSheet({ returnRequest, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [reviewerId, setReviewerId] = useState<string>('');
  const [approveNote, setApproveNote] = useState('');
  const [rejectNote, setRejectNote] = useState('');

  const detailQuery = useQuery({
    queryKey: ['returns', returnRequest?.id],
    queryFn: () => returnsService.get(returnRequest!.id),
    enabled: open && !!returnRequest?.id,
    refetchInterval: open ? 10_000 : false,
  });
  const ret = detailQuery.data ?? returnRequest;

  const usersQuery = useQuery({
    queryKey: ['platform', 'users', 'reviewer-picker'],
    queryFn: () => platformUsersService.list({ pageSize: 200 }),
    enabled: open && ret?.status === 'REQUESTED',
  });

  useEffect(() => {
    if (!open) {
      setReviewerId('');
      setApproveNote('');
      setRejectNote('');
    }
  }, [open]);

  const onError = (err: unknown) => {
    const e = toApiError(err);
    toast.error(e.message || 'Request failed');
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['returns'] });
  };

  const assign = useMutation({
    mutationFn: () =>
      returnsService.assignReviewer(ret!.id, { reviewerUserId: reviewerId }),
    onSuccess: () => {
      invalidate();
      toast.success('Reviewer assigned');
    },
    onError,
  });

  const approve = useMutation({
    mutationFn: () =>
      returnsService.approve(ret!.id, {
        reviewerNote: approveNote || undefined,
      }),
    onSuccess: () => {
      invalidate();
      toast.success('Approved · physical return order created');
    },
    onError,
  });

  const reject = useMutation({
    mutationFn: () => returnsService.reject(ret!.id, { reviewerNote: rejectNote }),
    onSuccess: () => {
      invalidate();
      toast.success('Return rejected');
    },
    onError,
  });

  const reviewerOptions = useMemo(
    () => (usersQuery.data?.items ?? []).filter((u) => u.status === 'active'),
    [usersQuery.data],
  );

  if (!ret) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-2xl overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            <span className="font-mono text-[14px]">{ret.returnNumber}</span>
            <Badge variant="warning" className="text-[10px]">
              {ret.status}
            </Badge>
          </SheetTitle>
          <SheetDescription>
            Customer return request against order{' '}
            <span className="font-mono">{ret.salesOrder.orderNumber}</span>
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="overview" className="mt-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="items">Items ({ret.items.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 pt-4">
            <Field label="Customer">
              <div>{ret.customer?.name ?? '—'}</div>
              <div className="font-mono text-[11px] text-muted-foreground">
                {ret.customer?.code ?? '—'}
              </div>
            </Field>
            <Field label="Order">
              <span className="font-mono">{ret.salesOrder.orderNumber}</span>
            </Field>
            <Field label="Requested at">
              <span className="font-mono">{fmt.dateTime(ret.createdAt)}</span>
            </Field>
            {ret.externalReturnRef && (
              <Field label="Portal ref">
                <span className="font-mono text-[11.5px]">{ret.externalReturnRef}</span>
              </Field>
            )}
            {ret.reason && (
              <Field label="Customer reason">
                <span className="whitespace-pre-wrap">{ret.reason}</span>
              </Field>
            )}
            {ret.reviewerNote && (
              <Field label="Reviewer note">
                <span className="whitespace-pre-wrap">{ret.reviewerNote}</span>
              </Field>
            )}

            {ret.status === 'REQUESTED' && (
              <div className="space-y-3 rounded-lg border border-border/60 bg-muted/10 p-3">
                <div className="text-[12px] font-semibold text-foreground">Review actions</div>

                {!ret.assignedReviewerId ? (
                  <div className="space-y-2">
                    <Select value={reviewerId} onValueChange={setReviewerId}>
                      <SelectTrigger className="h-9 text-[13px]">
                        <SelectValue placeholder="Pick a reviewer …" />
                      </SelectTrigger>
                      <SelectContent>
                        {reviewerOptions.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.fullName ?? u.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={() => assign.mutate()}
                      disabled={!reviewerId || assign.isPending}
                    >
                      {assign.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <UserPlus className="h-3.5 w-3.5" />
                      )}
                      Assign reviewer
                    </Button>
                  </div>
                ) : (
                  <div className="text-[12px] text-muted-foreground">
                    Currently assigned. Proceed with a decision below.
                  </div>
                )}

                <Separator />

                <div className="space-y-2">
                  <label className="text-[11px] font-medium text-foreground">
                    Approve — reviewer note (optional)
                  </label>
                  <Textarea
                    value={approveNote}
                    onChange={(e) => setApproveNote(e.target.value)}
                    rows={2}
                    placeholder="Approved — customer to use prepaid return label"
                    className="text-[13px]"
                  />
                  <Button size="sm" onClick={() => approve.mutate()} disabled={approve.isPending}>
                    {approve.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    )}
                    Approve return
                  </Button>
                </div>

                <div className="space-y-2">
                  <label className="text-[11px] font-medium text-foreground">
                    Reject — reviewer note (required)
                  </label>
                  <Textarea
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                    rows={2}
                    placeholder="Outside the 7-day return window — customer was informed"
                    className="text-[13px]"
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => reject.mutate()}
                    disabled={!rejectNote.trim() || reject.isPending}
                  >
                    {reject.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5" />
                    )}
                    Reject
                  </Button>
                </div>
              </div>
            )}

            {ret.status === 'APPROVED' && (
              <div className="rounded border border-info/30 bg-info/5 p-3 text-[12px]">
                Approved. The return is now a physical return order on the{' '}
                <strong>Return Orders</strong> page — track scan-inward, QC, and refund eligibility there.
              </div>
            )}
            {ret.status === 'REJECTED' && (
              <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-[12px]">
                Rejected. The customer has been notified via{' '}
                <span className="font-mono">return.rejected</span> webhook.
              </div>
            )}
          </TabsContent>

          <TabsContent value="items" className="pt-4">
            <table className="w-full text-[12.5px]">
              <thead className="border-b border-border/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 text-left">SKU</th>
                  <th className="px-2 py-1.5 text-left">Item</th>
                  <th className="px-2 py-1.5 text-right">Qty</th>
                  <th className="px-2 py-1.5 text-left">Reason</th>
                </tr>
              </thead>
              <tbody>
                {ret.items.map((i) => (
                  <tr key={i.id} className="border-b border-border/30 last:border-0">
                    <td className="px-2 py-1.5 font-mono">{i.sku}</td>
                    <td className="px-2 py-1.5">{i.name}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{i.quantity}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{i.reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 text-[13px]">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div>{children}</div>
    </div>
  );
}
